import React, { useState, useEffect } from 'react';
import { Phone, Mail, Globe, Search, ShoppingBag, Menu, X, ChevronDown, User } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import SearchModal from './SearchModal';

const NAV = [
  { name: 'Home', nameId: 'Beranda', link: '/#home', children: [] },
  { name: 'History', nameId: 'Sejarah', link: '/sejarah', children: [
    { name: 'Brand Introduction', link: '/sejarah#brand-introduction' },
    { name: '30-year history', link: '/sejarah#history-30-year' },
    { name: 'Productive Strength', link: '/sejarah#productive-strength' },
  ]},
  { name: 'Products', nameId: 'Produk', link: '/produk', children: [
    { name: 'Fusion Splicing', link: '/produk/fusion-splicing' },
    { name: 'Instruments', link: '/produk/instruments' },
  ]},
  { name: 'News', nameId: 'Berita', link: '/#news', children: [] },
  { name: 'Supports', nameId: 'Dukungan', link: '/dukungan', children: [
    { name: 'Warranty Policy', link: '/dukungan#warranty' },
    { name: 'Product Manual', link: '/dukungan#manual' },
    { name: 'Video Tutorials', link: '/dukungan#tutorial' },
  ]},
  { name: 'Contact', nameId: 'Kontak', link: '/kontak', children: [] },
];

const LOGO = 'https://customer-assets.emergentagent.com/job_jilong-branch-id/artifacts/oh3wbs3f_LOGO-JILONG-INDONESI-white-150x58-1.png';

const Header = () => {
  const { config } = useSiteContent();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeHash, setActiveHash] = useState('#home');
  const [searchOpen, setSearchOpen] = useState(false);
  const [langMenu, setLangMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (e, link) => {
    // Cross-route hash links like "/#home" — allow default nav when NOT on home page
    if (link.startsWith('/#')) {
      const hash = link.substring(1); // "#home"
      if (window.location.pathname === '/') {
        e.preventDefault();
        const el = hash === '#home' ? null : document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
        setActiveHash(hash);
        setMobileOpen(false);
      }
      // else: let browser navigate to /#hash
      return;
    }
    // Legacy plain hash (kept for safety)
    if (link.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(link);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveHash(link);
      } else if (link === '#home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setActiveHash(link);
      }
      setMobileOpen(false);
    }
    // Route links (/produk, /produk/xxx) — let react-router handle via anchor href
  };

  const phone = config.phone || '+62 821 4387 1568';
  const email = config.email || 'servicecenter@jilong-indonesia.com';
  const whatsapp = (config.whatsapp || phone).replace(/[^0-9]/g, '');
  const storeUrl = config.online_store_url || 'https://jilongot.com/';
  const anniversaryUrl = config.anniversary_url || 'https://jilongot.com/about/';

  return (
    <header className="w-full sticky top-0 z-50" data-testid="site-header">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-[#0a3d8f]" />
              <a href={`tel:${phone}`} className="font-medium hover:text-[#0a3d8f] transition-colors" data-testid="header-phone">
                {phone}
              </a>
              <span className="text-gray-500 hidden sm:inline">{config.phone_label || '(Service Center)'}</span>
            </div>
            <div className="flex items-center gap-4">
              <a href={`mailto:${email}`} className="hidden md:flex items-center gap-2 hover:text-[#0a3d8f] transition-colors" data-testid="header-email">
                <Mail size={14} className="text-[#0a3d8f]" />
                <span>{email}</span>
              </a>

              <div className="relative">
                <button
                  onClick={() => setLangMenu((v) => !v)}
                  className="flex items-center gap-1 hover:text-[#0a3d8f] transition-colors"
                  aria-label="Language"
                  data-testid="header-lang-btn"
                >
                  <Globe size={14} />
                  <span className="font-medium">ID</span>
                  <ChevronDown size={12} />
                </button>
                {langMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-white border border-gray-100 rounded-md shadow-xl z-50 min-w-[120px] py-1">
                    <button
                      onClick={() => setLangMenu(false)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-[#0a3d8f] font-semibold"
                      data-testid="lang-id"
                    >🇮🇩 Indonesia</button>
                    <a
                      href="https://jilongot.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="block px-4 py-2 text-sm hover:bg-blue-50 text-gray-700"
                      data-testid="lang-cn"
                    >🇨🇳 中文 (China)</a>
                    <a
                      href="https://en.jilongot.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="block px-4 py-2 text-sm hover:bg-blue-50 text-gray-700"
                      data-testid="lang-en"
                    >🇬🇧 English</a>
                  </div>
                )}
              </div>

              <button
                onClick={() => setSearchOpen(true)}
                className="hover:text-[#0a3d8f] transition-colors"
                aria-label="Search"
                data-testid="header-search-btn"
              >
                <Search size={16} />
              </button>

              <a
                href="/admin/login"
                className="hover:text-[#0a3d8f] transition-colors hidden md:inline-flex items-center gap-1"
                title="Admin"
                data-testid="header-admin-link"
              >
                <User size={14} />
              </a>

              {/* Online Store button hidden per request */}
            </div>
          </div>
        </div>
      </div>

      <div className={`bg-[#0a3d8f] text-white ${scrolled ? 'shadow-lg' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <a href="#home" onClick={(e) => handleNav(e, '#home')} data-testid="header-logo">
                <img src={LOGO} alt="Jilong Indonesia" className="h-12 w-auto" />
              </a>
              <a
                href={anniversaryUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="header-anniversary"
                className="anniversary-badge hidden sm:inline-flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-full cursor-pointer group"
                title="33 Tahun JILONG — Inovasi & Dedikasi"
              >
                <span className="anniversary-number">33</span>
                <span className="flex flex-col leading-tight text-left">
                  <span className="anniversary-tagline-top">Tahun JILONG</span>
                  <span className="anniversary-tagline-bottom">Inovasi &amp; Dedikasi</span>
                </span>
              </a>
            </div>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((item) => (
                <div key={item.name} className="dropdown-parent relative">
                  <a
                    href={item.link}
                    onClick={(e) => handleNav(e, item.link)}
                    data-testid={`nav-link-${item.name.toLowerCase()}`}
                    className={`nav-link px-4 py-2 inline-flex items-center gap-1 text-[15px] font-medium ${activeHash === item.link ? 'active' : ''}`}
                  >
                    {item.nameId}
                    {item.children.length > 0 && <ChevronDown size={14} className="opacity-70" />}
                  </a>
                  {item.children.length > 0 && (
                    <div className="dropdown-menu absolute top-full left-1/2 -translate-x-1/2 pt-2 min-w-[220px]">
                      <div className="bg-white text-gray-800 rounded-md shadow-2xl border border-gray-100 overflow-hidden py-2">
                        {item.children.map((child) => (
                          <a key={child.name} href={child.link} onClick={(e) => handleNav(e, child.link)}
                             className="block px-5 py-2.5 text-sm hover:bg-blue-50 hover:text-[#0a3d8f] transition-colors">
                            {child.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" data-testid="header-mobile-toggle">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-[#082e6b] mobile-menu border-t border-blue-900" data-testid="header-mobile-menu">
            <div className="max-w-7xl mx-auto px-4 py-4">
              {NAV.map((item) => (
                <div key={item.name} className="py-1">
                  <a href={item.link} onClick={(e) => handleNav(e, item.link)} className="block py-2 text-white font-medium">
                    {item.nameId}
                  </a>
                </div>
              ))}
              {/* Online Store button hidden per request */}
              <a
                href="/admin/login"
                className="block mt-2 bg-white/10 text-white text-center py-2 rounded-md font-semibold"
                data-testid="mobile-admin-link"
              >
                Admin Login →
              </a>
            </div>
          </div>
        )}
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} action={config.search_url || 'https://jilongot.com/search.html'} />
    </header>
  );
};

export default Header;
